import { useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Link, useHistory } from "react-router-dom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import logo from "../../icons/ix_logo.png";
import { config } from "../../utils/config";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_ERROR_MSG = "Please enter a valid email address !";

const AUTH_STYLES = `
.lgn-wrap {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(12px, 3vh, 26px);
  background: radial-gradient(1200px 600px at 12% 0%, #2a1657 0%, rgba(42,22,87,0) 60%),
              linear-gradient(160deg, #150b2e 0%, #24123f 40%, #3a2482 100%);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
}
.lgn-card {
  width: 100%;
  max-width: 1000px;
  height: 100%;
  max-height: 640px;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  background: #ffffff;
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 50px 100px -30px rgba(10, 4, 30, 0.65);
}

/* ---------- Brand panel ---------- */
.lgn-brand {
  position: relative;
  padding: 40px 42px;
  color: #f4f1ff;
  overflow: hidden;
  background: linear-gradient(155deg, #1c1038 0%, #3d2496 48%, #7c5cfc 105%);
  display: flex;
  flex-direction: column;
}
.lgn-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
  opacity: 0.55;
  pointer-events: none;
}
.lgn-blob-1 {
  width: 320px; height: 320px;
  right: -110px; top: -120px;
  background: radial-gradient(circle at 30% 30%, #9d7bff, rgba(124,92,252,0));
  animation: lgn-float 9s ease-in-out infinite;
}
.lgn-blob-2 {
  width: 240px; height: 240px;
  left: -90px; bottom: -80px;
  background: radial-gradient(circle at 60% 40%, #5b3fc8, rgba(76,47,179,0));
  animation: lgn-float 11s ease-in-out infinite reverse;
}
.lgn-grid-overlay {
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
  background-size: 46px 46px;
  mask-image: radial-gradient(600px 400px at 30% 25%, #000 0%, transparent 80%);
  pointer-events: none;
}
@keyframes lgn-float {
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-22px) translateX(10px); }
}
.lgn-brand-inner { position: relative; z-index: 1; display: flex; flex-direction: column; flex: 1; min-height: 0; }
.lgn-logo-row { display: flex; align-items: center; gap: 13px; margin-bottom: auto; }
.lgn-logo-badge {
  flex: 0 0 auto; width: 46px; height: 46px; border-radius: 13px;
  background: #ffffff; display: grid; place-items: center;
  box-shadow: 0 8px 20px -6px rgba(0,0,0,0.35);
}
.lgn-logo-badge img { width: 30px; height: 30px; object-fit: contain; }
.lgn-logo-row span {
  font-family: 'Sora', 'Inter', sans-serif; font-weight: 700; font-size: 17px; letter-spacing: 0.02em;
}
.lgn-headline {
  font-family: 'Sora', 'Inter', sans-serif;
  font-size: 27px; line-height: 1.22; font-weight: 700; margin: 22px 0 12px;
  letter-spacing: -0.01em;
}
.lgn-sub { font-size: 13px; line-height: 1.55; color: #cbbdf5; max-width: 360px; margin: 0 0 22px; }
.lgn-feats { display: flex; flex-direction: column; gap: 12px; margin-bottom: auto; }
.lgn-feat { display: flex; align-items: center; gap: 12px; font-size: 13.5px; color: #e7e0ff; }
.lgn-feat i {
  flex: 0 0 auto; width: 26px; height: 26px; border-radius: 8px;
  display: grid; place-items: center;
  background: rgba(255,255,255,0.14); border: 1px solid rgba(255,255,255,0.22);
}
.lgn-feat svg { width: 14px; height: 14px; }
.lgn-brand-foot { font-size: 11.5px; color: #a892e8; letter-spacing: 0.04em; margin-top: 22px; }

/* ---------- Form panel ---------- */
.lgn-form-panel { padding: 40px 48px; display: flex; flex-direction: column; justify-content: center; overflow-y: auto; }
.lgn-form-logo { display: none; }
.lgn-title { font-family: 'Sora', 'Inter', sans-serif; font-size: 24px; font-weight: 700; color: #1F2430; margin: 0; }
.lgn-title-sub { font-size: 13px; color: #6B7280; margin: 8px 0 30px; line-height: 1.5; }
.lgn-label {
  display: block; font-size: 11.5px; font-weight: 700; color: #4B5563;
  text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 7px;
}
.lgn-field { position: relative; margin-bottom: 18px; }
.lgn-field > svg.lgn-lead {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  color: #9CA3AF; width: 19px; height: 19px; pointer-events: none;
}
.lgn-input {
  width: 100%; height: 48px; padding: 0 14px 0 42px;
  border-radius: 12px; border: 1px solid #E3E6EC;
  font-size: 14px; color: #1F2430; background: #FCFCFD;
  outline: none; box-sizing: border-box;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.lgn-input::placeholder { color: #9CA3AF; }
.lgn-input:hover { border-color: #CBD0DA; }
.lgn-input:focus { border-color: #7c5cfc; background: #ffffff; box-shadow: 0 0 0 4px rgba(124,92,252,0.14); }
.lgn-btn {
  width: 100%; height: 48px; border: none; border-radius: 12px;
  background: linear-gradient(135deg, #4c2fb3 0%, #7c5cfc 100%);
  color: #fff; font-size: 14px; font-weight: 700; letter-spacing: 0.02em;
  cursor: pointer; box-shadow: 0 16px 30px -8px rgba(76,47,179,0.5);
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  margin-top: 4px;
}
.lgn-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 20px 36px -8px rgba(76,47,179,0.55); }
.lgn-btn:active:not(:disabled) { transform: translateY(0); }
.lgn-btn:disabled { opacity: 0.6; cursor: not-allowed; box-shadow: none; }
.lgn-back {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 24px; padding-top: 18px; border-top: 1px solid #EEF0F6;
  width: 100%;
}
.lgn-back a { font-size: 13px; color: #4c2fb3; font-weight: 600; text-decoration: none; }
.lgn-back a:hover { text-decoration: underline; }

@media (max-width: 860px) {
  .lgn-wrap { height: auto; min-height: 100vh; min-height: 100dvh; overflow-y: auto; }
  .lgn-card { grid-template-columns: 1fr; max-width: 440px; height: auto; max-height: none; }
  .lgn-brand { display: none; }
  .lgn-form-panel { padding: 40px 30px; overflow-y: visible; }
  .lgn-form-logo { display: block; text-align: center; margin-bottom: 20px; }
  .lgn-form-logo img { height: 46px; }
}

@media (min-width: 861px) and (max-height: 640px) {
  .lgn-headline { font-size: 24px; margin: 20px 0 10px; }
  .lgn-sub { margin-bottom: 20px; }
  .lgn-feats { gap: 10px; }
  .lgn-brand-foot { margin-top: 20px; }
  .lgn-title-sub { margin-bottom: 20px; }
}
`;

const ForgetPassword = () => {
  const { apiEndpoint, baseNodeapiUrl } = config;
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isMailVerified, setisMailVerified] = useState(false);
  const [user, setUser] = useState({ email: "" });
  const [errorMessage, setErrorMessage] = useState({ email: true });

  const forgetPassMannage = async () => {
    const { data } = await axios({
      url: "/user/forget",
      method: "POST",
      data: user,
    });
    setLoading(false);
    if (data.message === "flag2") {
      setUser({ email: "" });
      toast.success("Please check your email to reset password !", {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else if (data.message === "flag1") {
      toast.error("Email is not registered !", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else if (data.message === "flag0") {
      toast.error("404 error", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const forgetPassHandler = (e: any) => {
    e.preventDefault();
    setLoading(true);
    const email = user.email.trim();
    if (!email) {
      setLoading(false);
      toast.error("Email is required !", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else if (!EMAIL_REGEX.test(email)) {
      setLoading(false);
      toast.error(EMAIL_ERROR_MSG, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } else {
      forgetPassMannage();
    }
  };

  const onChangeHandler = async (e: any) => {
    const value = e.target.value;
    setUser({ ...user, [e.target.name]: value });
    setErrorMessage({
      ...errorMessage,
      [e.target.name]: !value.trim() || !EMAIL_REGEX.test(value.trim()),
    });
  };

  async function handleSubmit(e: any) {
    if (e && e.preventDefault) e.preventDefault();
    const email = user.email.trim();
    if (!email) {
      setErrorMessage({ ...errorMessage, email: true });
      toast.error("Email is required !");
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setErrorMessage({ ...errorMessage, email: true });
      toast.error(EMAIL_ERROR_MSG);
      return;
    }
    setErrorMessage({ ...errorMessage, email: false });
    try {
      setLoading(true);
      const { data } = await axios({
        url: `${apiEndpoint}/checkemail`,
        method: "POST",
        headers: {
          "x-custom-authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`,
        },
        data: { email },
      });

      if (data.status === 403) {
        toast.error("Mail Doesnot Exist In our Database!");
        return;
      }

      if (data.code === "200") {
        const { data: resetData } = await axios({
          url: `${baseNodeapiUrl}/admin/api/resetPassword`,
          method: "POST",
          headers: {
            "x-custom-authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`,
          },
          data: {
            email_id: email,
            url: `${baseNodeapiUrl}/resetpassword?I5s3kVT3Yaqs67n=${email}`,
          },
        });

        console.log("resetData", resetData);

        if (resetData.status === "200") {
          toast.success("We have sent a password reset link on your mail!");
          setisMailVerified(true);
          setTimeout(() => history.push("/"), 2000);
        }
      }
    } catch (error) {
      toast.error("Something Went Wrong !");
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const Check = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );

  return (
    <>
      <style>{AUTH_STYLES}</style>

      <div className="lgn-wrap">
        <div className="lgn-card">
          {/* Brand side */}
          <div className="lgn-brand">
            <span className="lgn-blob lgn-blob-1" />
            <span className="lgn-blob lgn-blob-2" />
            <span className="lgn-grid-overlay" />

            <div className="lgn-brand-inner">
              <div className="lgn-logo-row">
                <span className="lgn-logo-badge"><img src={logo} alt="Inventoryless" /></span>
                <span>Inventoryless</span>
              </div>

              <h1 className="lgn-headline">
                Locked out?<br />Let&apos;s get you back in.
              </h1>
              <p className="lgn-sub">
                We&apos;ll email a secure reset link to your registered address so you can set a new password in seconds.
              </p>

              <div className="lgn-feats">
                <div className="lgn-feat"><i><Check /></i> Link expires automatically for safety</div>
                <div className="lgn-feat"><i><Check /></i> Only your registered email can reset</div>
                <div className="lgn-feat"><i><Check /></i> Your dashboard access stays protected</div>
              </div>

              <div className="lgn-brand-foot">© {new Date().getFullYear()} Inventoryless · Seller Admin</div>
            </div>
          </div>

          {/* Form side */}
          <div className="lgn-form-panel">
            <div className="lgn-form-logo">
              <img src={logo} alt="Inventoryless" />
            </div>

            <h2 className="lgn-title">Forgot your password?</h2>
            <p className="lgn-title-sub">
              Enter your registered email and we&apos;ll send you a link to reset it.
            </p>

            <form onSubmit={handleSubmit}>
              <label className="lgn-label">Email address</label>
              <div className="lgn-field">
                <PersonOutlineIcon className="lgn-lead" />
                <input
                  className="lgn-input"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={user.email}
                  onChange={(e) => onChangeHandler(e)}
                  aria-invalid={errorMessage.email}
                  required
                  autoComplete="username"
                />
              </div>

              <button className="lgn-btn" type="submit" disabled={loading}>
                {loading ? "Sending link..." : "Send reset link"}
              </button>
            </form>

            <div className="lgn-back">
              <Link to="/">&#8592; Back to login</Link>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  );
};

export default ForgetPassword;
